// Bouwstenen voor reviews (plan §6.9): sterren met cijfer, verdeling, bronmerk, stappenbalk, voorbeeldframe en periode.
// Cijfer altijd als tekst; sterren zijn decoratie (aria-hidden) met vulling via SVG-maskers, nooit alleen kleur.
import { useId, useState, type CSSProperties, type ReactNode } from "react";
import { ArrowRightIcon, CaretLeftIcon, CaretRightIcon, CalendarBlankIcon, CheckIcon, DesktopIcon, DeviceMobileIcon } from "@phosphor-icons/react";
import { parseDate, type CalendarDate } from "@internationalized/date";
import {
  Button as AriaButton,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DateRangePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
  RangeCalendar,
} from "react-aria-components";
import { cx } from "../cx.ts";
import { Feedback, FieldLabel, Segmented, type FieldBase } from "./fields.tsx";

// ---------------------------------------------------------------- sterren
const STAR = "M12 2.5l2.9 6.06 6.6.82-4.86 4.57 1.24 6.55L12 17.3l-5.88 3.2 1.24-6.55L2.5 9.38l6.6-.82z";

/** Vijf sterren met gedeeltelijke vulling (halve sterren, tienpuntsschaal omgerekend). Decoratief: het cijfer staat ernaast. */
export function Stars({ value, scale = 5, size = 16, className }: { value: number; scale?: number; size?: number; className?: string }) {
  const id = useId().replace(/:/g, "");
  const five = scale === 5 ? value : (value / scale) * 5;
  const width = Math.max(0, Math.min(5, five)) * 24;
  return (
    <svg className={cx("ui-stars", className)} viewBox="0 0 120 24" width={size * 5} height={size} aria-hidden focusable="false">
      <defs>
        <mask id={`m${id}`}>
          {[0, 1, 2, 3, 4].map((i) => (
            <path key={i} d={STAR} transform={`translate(${i * 24} 0)`} fill="white" />
          ))}
        </mask>
      </defs>
      <rect className="ui-stars__empty" x="0" y="0" width="120" height="24" mask={`url(#m${id})`} />
      <rect className="ui-stars__fill" x="0" y="0" width={width} height="24" mask={`url(#m${id})`} />
    </svg>
  );
}

/** Sterren plus cijfer als tekst ("4,5" of "8/10"); `label` is de volledige toegankelijke tekst. */
export function Rating({ value, scale = 5, locale = "nl-NL", size = "md", label, count, className }: { value: number; scale?: number; locale?: string; size?: "sm" | "md" | "lg"; label: string; count?: ReactNode; className?: string }) {
  const text = scale === 5 ? new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value) : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}/${scale}`;
  const px = size === "lg" ? 22 : size === "sm" ? 13 : 16;
  return (
    <span className={cx("ui-rating", `ui-rating--${size}`, className)} title={label}>
      <Stars value={value} scale={scale} size={px} />
      <span className="ui-rating__value num" aria-hidden>
        {text}
      </span>
      {count != null ? (
        <span className="ui-rating__count" aria-hidden>
          {count}
        </span>
      ) : null}
      <span className="sr-only">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------- verdeling 5→1
export function RatingDistribution({ counts, label, starLabel, locale = "nl-NL" }: { counts: Record<string, number>; label: string; starLabel: (n: number) => string; locale?: string }) {
  const total = Math.max(1, Object.values(counts).reduce((a, b) => a + b, 0));
  const fmt = new Intl.NumberFormat(locale);
  return (
    <figure className="ui-ratingdist" aria-label={label}>
      <ul>
        {[5, 4, 3, 2, 1].map((n, i) => {
          const v = counts[String(n)] ?? 0;
          return (
            <li key={n} style={{ ["--i" as string]: i } as CSSProperties}>
              <span className="ui-ratingdist__label">{starLabel(n)}</span>
              <span className="ui-ratingdist__track" aria-hidden>
                <span className="ui-ratingdist__bar" style={{ width: `${(v / total) * 100}%` }} />
              </span>
              <span className="ui-ratingdist__value num">{fmt.format(v)}</span>
            </li>
          );
        })}
      </ul>
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {[5, 4, 3, 2, 1].map((n) => (
            <tr key={n}>
              <th scope="row">{starLabel(n)}</th>
              <td>{counts[String(n)] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

// ---------------------------------------------------------------- bronmerk
/**
 * Merkteken van een reviewbron in een tegel. Officiële logo's per merkrichtlijn komen in `brand/sources/` zodra de
 * assets met herkomst er zijn; tot dan een neutrale tegel met de beginletter en de naam als toegankelijke tekst, zodat
 * BRES geen merk nabootst.
 */
/** Het echte Google-logo (de gekleurde G). Nooit een eenkleurige letter of icoon voor Google. */
export function GoogleMark({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg className={cx("ui-googlemark", className)} width={size} height={size} viewBox="0 0 48 48" aria-hidden focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export function SourceMark({ source, name, size = 32, className }: { source: string; name: string; size?: number; className?: string }) {
  const letter = source === "bres_reviews" ? "★" : source === "legacy_reviews" ? "↺" : source === "bres_test_reviews" ? "T" : name.charAt(0).toUpperCase();
  return (
    <span className={cx("ui-sourcemark", className)} data-source={source} style={{ ["--mark" as string]: `${size}px` } as CSSProperties} role="img" aria-label={name} title={name}>
      {source.startsWith("google") ? <GoogleMark size={Math.round(size * 0.56)} /> : <span aria-hidden>{letter}</span>}
    </span>
  );
}

// ---------------------------------------------------------------- stappenbalk
export function Steps({ steps, current, label }: { steps: string[]; current: number; label: string }) {
  return (
    <ol className="ui-steps" aria-label={label}>
      {steps.map((s, i) => (
        <li key={s} className="ui-steps__item" data-state={i < current ? "done" : i === current ? "current" : "todo"} aria-current={i === current ? "step" : undefined}>
          <span className="ui-steps__dot" aria-hidden>
            {i < current ? <CheckIcon weight="bold" /> : i + 1}
          </span>
          <span className="ui-steps__label">{s}</span>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------- voorbeeldframe
export function PreviewFrame({ src, title, mobileLabel, desktopLabel, deviceLabel, loading, loadingLabel }: { src: string | null; title: string; mobileLabel: string; desktopLabel: string; deviceLabel: string; loading?: boolean; loadingLabel: string }) {
  const [device, setDevice] = useState("desktop");
  return (
    <div className="ui-preview" data-device={device}>
      <div className="ui-preview__bar">
        <Segmented
          label={deviceLabel}
          value={device}
          onChange={setDevice}
          options={[
            { id: "desktop", label: desktopLabel, icon: <DesktopIcon aria-hidden /> },
            { id: "mobile", label: mobileLabel, icon: <DeviceMobileIcon aria-hidden /> },
          ]}
        />
      </div>
      <div className="ui-preview__stage" aria-busy={loading || undefined}>
        {loading ? <span className="ui-preview__line" role="progressbar" aria-label={loadingLabel} /> : null}
        {src ? <iframe className="ui-preview__frame" src={src} title={title} loading="lazy" sandbox="allow-scripts allow-same-origin" referrerPolicy="no-referrer" /> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- periode
export interface DateRangeFieldProps extends FieldBase {
  value: { start: string; end: string } | null;
  onChange: (value: { start: string; end: string } | null) => void;
  isDisabled?: boolean;
}

export function DateRangeField({ label, description, errorMessage, optionalText, className, value, onChange, isDisabled }: DateRangeFieldProps) {
  let current: { start: CalendarDate; end: CalendarDate } | null = null;
  try {
    current = value ? { start: parseDate(value.start), end: parseDate(value.end) } : null;
  } catch {
    current = null;
  }
  return (
    <DateRangePicker
      className={cx("ui-field ui-date", className)}
      value={current}
      onChange={(next) => onChange(next ? { start: next.start.toString(), end: next.end.toString() } : null)}
      isDisabled={isDisabled}
      shouldForceLeadingZeros
    >
      <FieldLabel label={label} optionalText={optionalText} />
      <Group className="ui-input-group ui-date__group">
        <DateInput slot="start" className="ui-date__input">{(segment) => <DateSegment segment={segment} className="ui-date__segment" />}</DateInput>
        <ArrowRightIcon aria-hidden className="ui-date__dash" />
        <DateInput slot="end" className="ui-date__input">{(segment) => <DateSegment segment={segment} className="ui-date__segment" />}</DateInput>
        <AriaButton className="ui-date__button">
          <CalendarBlankIcon aria-hidden />
        </AriaButton>
      </Group>
      <Feedback description={description} errorMessage={errorMessage} />
      <Popover className="ui-popover ui-date__popover" placement="bottom end">
        <Dialog className="ui-date__dialog">
          <RangeCalendar className="ui-calendar">
            <header className="ui-calendar__head">
              <AriaButton slot="previous" className="ui-calendar__nav">
                <CaretLeftIcon aria-hidden />
              </AriaButton>
              <Heading className="ui-calendar__title" />
              <AriaButton slot="next" className="ui-calendar__nav">
                <CaretRightIcon aria-hidden />
              </AriaButton>
            </header>
            <CalendarGrid className="ui-calendar__grid" weekdayStyle="short">
              <CalendarGridHeader>{(day) => <CalendarHeaderCell className="ui-calendar__weekday">{day}</CalendarHeaderCell>}</CalendarGridHeader>
              <CalendarGridBody>{(date) => <CalendarCell date={date} className="ui-calendar__cell" />}</CalendarGridBody>
            </CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </DateRangePicker>
  );
}
